import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FileDropZone } from "./FileDropZone";

export const AssignmentGroupUploadDialog = ({
  open,
  setOpen,
  assignmentGroupId,
  onSuccess,
}) => {
  const ApiClient = useApiClient();
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (!submitting) {
      setOpen(false);
      setFiles([]);
    }
  };

  const handleFilesChange = (incoming, isRemoval = false) => {
    if (isRemoval) {
      setFiles(incoming);
      return;
    }

    const filtered = incoming.filter((f) =>
      f.name.toLowerCase().endsWith(".pdf"),
    );

    if (filtered.length !== incoming.length) {
      enqueueSnackbar("Only PDF files are accepted", {
        variant: "warning",
        autoHideDuration: 2500,
      });
    }

    if (filtered.length > 1) {
      enqueueSnackbar("Please upload only one PDF file", {
        variant: "warning",
      });
      return;
    }

    setFiles(filtered);
  };

  const handleSubmit = async () => {
    if (files.length === 0) return;

    setSubmitting(true);
    try {
      const result = await ApiClient.assignment.submitAssignmentGroupFile({
        assignmentGroup: assignmentGroupId,
        file: files[0],
      });

      if (result.success) {
        enqueueSnackbar(
          result.message || "Multi-assignment file submitted successfully",
          { variant: "success" },
        );
        setFiles([]);
        setOpen(false);
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        enqueueSnackbar(result.message || "Upload failed", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      enqueueSnackbar("Failed to upload file. Please try again.", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Multi-Assignment Booklets</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a single PDF containing student booklets for multiple
          assignments in this group. Each booklet must have a Fruitify cover
          sheet with a QR code identifying both the student and assignment.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            Important:
          </Typography>
          <Typography variant="body2">
            • Download cover sheets using "Download All Cover Sheets" button
            <br />
            • Each student booklet must start with their cover sheet
            <br />
            • Booklets from different assignment groups will be ignored
            <br />• Only PDF files are accepted
          </Typography>
        </Alert>

        <FileDropZone
          files={files}
          onFilesChange={handleFilesChange}
          multipleFiles={false}
          acceptedFormats=".pdf"
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={files.length === 0 || submitting}
        >
          {submitting ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
